import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Plus, Trash2, Loader2, Info } from 'lucide-react';
import LiteShell, { StepIndicator } from '../LiteShell';
import DeIdPanel from '@/components/lite/DeIdPanel';
import OutputView from '@/components/lite/OutputView';
import RateLimitGate from '@/components/lite/RateLimitGate';
import { liteApi } from '@/utils/lite/liteApi';

const AGE_RANGES = ['Under 3', '3-5', '5-8', '8-12', '12-18', 'Adult'];

export default function GLPGuide() {
  const [step, setStep] = useState(1);
  const [utterances, setUtterances] = useState(['', '', '', '', '']);
  const [context, setContext] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [output, setOutput] = useState('');
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState('');
  const [gateOpen, setGateOpen] = useState(false);
  const [gateInfo, setGateInfo] = useState({ used: 0, limit: 0 });
  const [cleanedContext, setCleanedContext] = useState('');

  const updateUtterance = (i, v) => {
    const arr = [...utterances];
    arr[i] = v;
    setUtterances(arr);
  };
  const addUtterance = () => utterances.length < 15 && setUtterances([...utterances, '']);
  const removeUtterance = (i) => utterances.length > 3 && setUtterances(utterances.filter((_, idx) => idx !== i));

  const goToDeId = () => {
    setError('');
    const filled = utterances.filter(u => u.trim());
    if (filled.length < 3) {
      setError('Please add at least 3 utterances.');
      return;
    }
    setStep(2);
  };

  const proceedAfterDeId = (cleaned) => {
    setCleanedContext(cleaned);
    setStep(3);
    runGenerate(cleaned);
  };

  const runGenerate = async (contextClean) => {
    setOutput('');
    setError('');
    try {
      let email = null;
      try { email = localStorage.getItem('playfirst_lite_email'); } catch {}
      const payload = {
        utterances: utterances.filter(u => u.trim()),
        context: contextClean,
        age_range: ageRange,
        confirm_deidentified: true,
        email,
      };
      const res = await liteApi.generateGlp(payload);
      setOutput(res.output);
      setUsage({ used_today: res.used_today, daily_limit: res.daily_limit });
      setStep(4);
    } catch (e) {
      if (e.status === 429) {
        const d = e.data?.detail || {};
        setGateInfo({ used: d.used || 0, limit: d.limit || 5 });
        setGateOpen(true);
        setStep(1);
      } else {
        setError(e.message || 'Generation failed.');
      }
    }
  };

  return (
    <LiteShell currentStep={step}>
      <h1 className="text-3xl font-serif text-primary-900 mb-2">GLP Support Guide</h1>
      <p className="text-stone-500 mb-6">Pattern-based GLP/NLA stage analysis with neurodiversity-affirming strategies.</p>
      <StepIndicator step={step} />

      <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p><strong>Not a diagnostic tool.</strong> Output is illustrative only. Formal stage identification benefits from longitudinal sampling (50–100+ utterances) and qualified clinical assessment.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="lite-glp-error">{error}</div>
      )}

      {step === 1 && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Age range</Label>
              <Select value={ageRange} onValueChange={setAgeRange}>
                <SelectTrigger data-testid="glp-age-select"><SelectValue placeholder="Select age range" /></SelectTrigger>
                <SelectContent>{AGE_RANGES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Sample utterances (5–10 recommended)</Label>
            <p className="text-xs text-stone-500 mb-2">Quote what the child actually said. Use "child" or "they" for any names.</p>
            <div className="space-y-2">
              {utterances.map((u, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-xs text-stone-400 mt-3 w-6">{i + 1}.</span>
                  <Input
                    placeholder='e.g. "To infinity and beyond!"'
                    value={u}
                    onChange={(e) => updateUtterance(i, e.target.value)}
                    data-testid={`glp-utterance-${i}`}
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeUtterance(i)} disabled={utterances.length <= 3} data-testid={`glp-utterance-remove-${i}`}>
                    <Trash2 className="w-4 h-4 text-stone-400" />
                  </Button>
                </div>
              ))}
              {utterances.length < 15 && (
                <Button variant="outline" size="sm" onClick={addUtterance} className="rounded-full" data-testid="glp-add-utterance-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add utterance
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Context (optional)</Label>
            <Textarea
              data-testid="glp-context-textarea"
              placeholder="Any helpful context — e.g. interests, regulation patterns, settings where utterances were observed."
              value={context}
              onChange={(e) => setContext(e.target.value)}
              rows={4}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={goToDeId} className="rounded-full" data-testid="glp-next-btn">
              Next: Clean &amp; review
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <DeIdPanel text={context || ' '} onProceed={proceedAfterDeId} onCancel={() => setStep(1)} />
      )}

      {step === 3 && (
        <div className="flex flex-col items-center justify-center py-16 text-stone-600" data-testid="lite-generating">
          <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
          <p className="font-medium">Analyzing GLP patterns…</p>
          <p className="text-sm text-stone-500 mt-1">This usually takes 10–20 seconds.</p>
        </div>
      )}

      {step === 4 && output && (
        <OutputView
          output={output}
          toolLabel="GLP Support Guide"
          tool="glp"
          usage={usage}
          onRegenerate={() => runGenerate(cleanedContext)}
          onStartOver={() => { setOutput(''); setStep(1); }}
          onWaitlist={() => { setGateInfo({ used: 0, limit: 0 }); setGateOpen(true); }}
        />
      )}

      <RateLimitGate
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        used={gateInfo.used}
        limit={gateInfo.limit}
        onUnlocked={() => setGateOpen(false)}
      />
    </LiteShell>
  );
}
